import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { collection, collectionGroup, getDocs, getCountFromServer, query, where, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import StatsCard from '../components/StatsCard';
import TopProducts from '../components/TopProducts';
import StockAlerts from '../components/StockAlerts';
import RecentSales from '../components/RecentSales';
import {
  addTo, companyName, dayKey, daysAgo, downloadCsv, errorMessage, formatDate,
  formatTotals, lastDays, unitPrice,
} from '../lib/format';

const SalesChart = React.lazy(() => import('../components/SalesChart'));

const PERIOD_DAYS = 30;

const parentId = (snap) => snap.ref.parent.parent?.id || 'N/A';

function buildDashboard({ companies, sales, products }) {
  const byId = Object.fromEntries(companies.map((c) => [c.id, c]));
  const currencyOf = (companyId) => byId[companyId]?.currency || 'USD';

  const salesTotals = {};
  const profitTotals = {};
  const todayTotals = {};
  const today = dayKey(Date.now());
  const salesCountByCurrency = {};

  sales.forEach((sale) => {
    const currency = currencyOf(sale.companyId);
    addTo(salesTotals, currency, sale.totalAmount);
    addTo(profitTotals, currency, (sale.totalAmount || 0) - (sale.totalCost || 0));
    if (sale.timestamp && dayKey(sale.timestamp) === today) addTo(todayTotals, currency, sale.totalAmount);
    salesCountByCurrency[currency] = (salesCountByCurrency[currency] || 0) + 1;
  });

  // Las gráficas no pueden sumar dólares con bolívares: muestran la moneda
  // con más ventas y lo dicen en el título.
  const chartCurrency =
    Object.entries(salesCountByCurrency).sort((a, b) => b[1] - a[1])[0]?.[0] || 'USD';
  const mixedCurrencies = Object.keys(salesCountByCurrency).length > 1;

  const days = lastDays(PERIOD_DAYS);
  const daily = Object.fromEntries(days.map((d) => [d, 0]));
  const byCompany = {};
  sales.forEach((sale) => {
    if (currencyOf(sale.companyId) !== chartCurrency) return;
    const key = sale.timestamp ? dayKey(sale.timestamp) : null;
    if (key in daily) daily[key] += sale.totalAmount || 0;
    byCompany[sale.companyId] = (byCompany[sale.companyId] || 0) + (sale.totalAmount || 0);
  });
  const topCompanies = Object.entries(byCompany).sort((a, b) => b[1] - a[1]).slice(0, 6);

  // Productos más vendidos. Se agrupa por tienda + producto: dos tiendas
  // pueden tener un «p-1» distinto.
  const productMap = {};
  sales.forEach((sale) => {
    (sale.items || []).forEach((item) => {
      const product = item.product || {};
      const key = `${sale.companyId}/${product.id || product.name}`;
      if (!productMap[key]) {
        productMap[key] = {
          name: product.name || 'Sin nombre',
          companyId: sale.companyId,
          currency: currencyOf(sale.companyId),
          quantity: 0,
          revenue: 0,
        };
      }
      const qty = Number(item.quantity) || 0;
      productMap[key].quantity += qty;
      productMap[key].revenue += unitPrice(product) * qty;
    });
  });
  const topProducts = Object.values(productMap).sort((a, b) => b.revenue - a.revenue).slice(0, 6);

  // Bajo mínimo, con la misma condición que Product.isLowStock en la app.
  const lowStock = products
    .filter((p) => p.trackInventory !== false && (Number(p.stock) || 0) <= (p.minStock ?? 5))
    .sort((a, b) => (a.stock || 0) - (b.stock || 0));

  return {
    salesTotals, profitTotals, todayTotals, chartCurrency, mixedCurrencies,
    salesByDay: { labels: days.map((d) => d.slice(8) + '/' + d.slice(5, 7)), data: days.map((d) => daily[d]) },
    salesByCompany: {
      labels: topCompanies.map(([id]) => companyName(byId[id] || { id })),
      data: topCompanies.map(([, total]) => total),
    },
    topProducts,
    lowStock,
  };
}

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [usersCount, setUsersCount] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const salesQuery = query(
      collectionGroup(db, 'sales'),
      where('timestamp', '>=', daysAgo(PERIOD_DAYS - 1)),
      orderBy('timestamp', 'desc'),
    );

    // Cada bloque por separado: antes un solo rechazo (productos) tumbaba el
    // Promise.all y el tablero se quedaba en cero sin decir por qué.
    const [companiesRes, salesRes, productsRes, usersRes] = await Promise.allSettled([
      getDocs(collection(db, 'companies')),
      getDocs(salesQuery),
      getDocs(collectionGroup(db, 'products')),
      getCountFromServer(collectionGroup(db, 'users')),
    ]);

    const failed = [];
    const take = (res, label, map, fallback) => {
      if (res.status === 'fulfilled') return map(res.value);
      console.error(`Error loading ${label}`, res.reason);
      failed.push(`${label}: ${errorMessage(res.reason)}`);
      return fallback;
    };

    setCompanies(take(companiesRes, 'Tiendas', (s) => s.docs.map((d) => ({ id: d.id, ...d.data() })), []));
    setSales(take(salesRes, 'Ventas', (s) => s.docs.map((d) => ({ id: d.id, companyId: parentId(d), ...d.data() })), []));
    setProducts(take(productsRes, 'Inventario', (s) => s.docs.map((d) => ({ id: d.id, companyId: parentId(d), ...d.data() })), []));
    setUsersCount(take(usersRes, 'Usuarios', (r) => r.data().count, null));
    setErrors(failed);
    setUpdatedAt(Date.now());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const data = buildDashboard({ companies, sales, products });
  const companyMap = Object.fromEntries(companies.map((c) => [c.id, c]));

  const handleExportCSV = () => {
    downloadCsv(
      `ventas-${dayKey(Date.now())}.csv`,
      ['Tienda', 'Fecha', 'Cajero', 'Método de pago', 'Total', 'Costo', 'Impuesto', 'Moneda', 'Recibo'],
      sales.map((s) => [
        companyName(companyMap[s.companyId] || { id: s.companyId }),
        formatDate(s.timestamp),
        s.cashierName || '',
        s.paymentMethod || '',
        s.totalAmount ?? 0,
        s.totalCost ?? 0,
        s.taxAmount ?? 0,
        companyMap[s.companyId]?.currency || 'USD',
        s.receiptId || s.id,
      ]),
    );
  };

  if (loading && !updatedAt) {
    return (
      <div className="skeleton-grid">
        {[1, 2, 3, 4].map((i) => <div key={i} className="skeleton-card" />)}
      </div>
    );
  }

  const margin = (() => {
    const s = data.salesTotals[data.chartCurrency] || 0;
    const p = data.profitTotals[data.chartCurrency] || 0;
    return s > 0 ? `Margen ${Math.round((p / s) * 100)} %` : null;
  })();

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Centro de control</h1>
          <p className="page-subtitle">
            Últimos {PERIOD_DAYS} días en todas las tiendas · actualizado {formatDate(updatedAt)}
          </p>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? <span className="spinner spinner-dark" /> : '↻'} Actualizar
        </button>
      </div>

      {errors.length > 0 && (
        <div className="notice notice-error">
          <strong>Parte del tablero no se pudo cargar.</strong>
          <ul>{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </div>
      )}

      <div className="stats-grid">
        <StatsCard title="Ventas" value={formatTotals(data.salesTotals)} icon="💰" subtitle={`${sales.length} ventas · hoy ${formatTotals(data.todayTotals)}`} />
        <StatsCard title="Ganancia bruta" value={formatTotals(data.profitTotals)} icon="📈" subtitle={margin} />
        <StatsCard title="Tiendas" value={companies.length} icon="🏬" subtitle={`${new Set(sales.map((s) => s.companyId)).size} con ventas en el periodo`} />
        <StatsCard title="Usuarios" value={usersCount ?? '—'} icon="👥" subtitle={data.lowStock.length === 1 ? '1 producto bajo mínimo' : `${data.lowStock.length} productos bajo mínimo`} />
      </div>

      <Suspense fallback={<div className="card chart-placeholder"><span className="spinner spinner-dark" /></div>}>
        <SalesChart
          salesByDay={data.salesByDay}
          salesByCompany={data.salesByCompany}
          currency={data.chartCurrency}
          mixedCurrencies={data.mixedCurrencies}
        />
      </Suspense>

      <div className="tables-grid">
        <TopProducts topProducts={data.topProducts} companyMap={companyMap} />
        <StockAlerts lowStockAlerts={data.lowStock} companyMap={companyMap} />
      </div>

      <RecentSales recentSales={sales.slice(0, 8)} companyMap={companyMap} onExport={sales.length ? handleExportCSV : null} exportCount={sales.length} />
    </div>
  );
}

export default Dashboard;
